/**
 * SubTabBar Component
 *
 * THE single, standardized secondary-navigation bar for the whole app.
 * Horizontal, top-positioned, identical on desktop and mobile (horizontal
 * scroll when the tabs overflow). Replaces the three legacy sub-nav patterns:
 *   - CategorySubNav   (vertical-left on desktop / pills on mobile)  [Video, Image]
 *   - the inline top-underline bar                                   [YouTube, Settings]
 *   - the DubbingTab pill bar
 *
 * Visual reference is the (already-responsive) YouTube tab bar: a bottom
 * border with an accent underline on the active tab.
 *
 * Optional `group` on a tab renders subtle inline section labels, so dense
 * sections (Image has 11 tabs) stay organised while sparse ones render flat.
 */

import { memo } from 'react';
import type { LucideIcon } from 'lucide-react';
import { SidebarBadge } from '../layout/SidebarBadge';

export interface SubTabItem {
  /** Unique tab id. */
  id: string;
  /** Resolved display label (already translated by the caller). */
  label: string;
  /** Optional leading icon. */
  icon?: LucideIcon;
  /** Optional section label — when present, tabs are grouped under inline headers. */
  group?: string;
  /** Optional active-generation count; renders a <SidebarBadge> over the icon. */
  badgeCount?: number;
  /** Hide the tab entirely (e.g. environment gating). */
  hidden?: boolean;
  /** Disable the tab. */
  disabled?: boolean;
  /** Optional test id; defaults to `subtab-${id}`. */
  testId?: string;
}

type Accent = 'purple' | 'blue';

interface SubTabBarProps {
  tabs: SubTabItem[];
  activeId: string;
  onChange: (id: string) => void;
  accent?: Accent;
  /** Extra classes for the outer container. */
  className?: string;
  /** Accessible label for the nav landmark. */
  ariaLabel?: string;
}

const ACCENT: Record<Accent, { active: string }> = {
  purple: { active: 'border-purple-400 text-white' },
  blue: { active: 'border-blue-400 text-white' },
};

function SubTabBarInner({
  tabs,
  activeId,
  onChange,
  accent = 'purple',
  className = '',
  ariaLabel = 'Section navigation',
}: SubTabBarProps) {
  const visibleTabs = tabs.filter((t) => !t.hidden);
  const accentClasses = ACCENT[accent].active;

  // Preserve declaration order while bucketing into groups (if any tab is grouped).
  const hasGroups = visibleTabs.some((t) => t.group);
  const groups: Array<{ label: string | null; tabs: SubTabItem[] }> = [];
  if (hasGroups) {
    for (const tab of visibleTabs) {
      const label = tab.group ?? null;
      const last = groups[groups.length - 1];
      if (last && last.label === label) {
        last.tabs.push(tab);
      } else {
        groups.push({ label, tabs: [tab] });
      }
    }
  } else {
    groups.push({ label: null, tabs: visibleTabs });
  }

  const renderTab = (tab: SubTabItem) => {
    const Icon = tab.icon;
    const active = activeId === tab.id;
    return (
      <button
        key={tab.id}
        type="button"
        onClick={() => !tab.disabled && onChange(tab.id)}
        disabled={tab.disabled}
        className={`min-h-[44px] px-3 sm:px-4 py-3 text-sm flex items-center gap-2 border-b-2 transition-colors whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed ${
          active ? accentClasses : 'border-transparent text-white/60 hover:text-white'
        }`}
        aria-selected={active}
        role="tab"
        data-testid={tab.testId ?? `subtab-${tab.id}`}
      >
        {Icon && (
          <span className="relative flex items-center">
            <Icon className="h-4 w-4" />
            {tab.badgeCount != null && <SidebarBadge count={tab.badgeCount} />}
          </span>
        )}
        <span>{tab.label}</span>
      </button>
    );
  };

  return (
    <div
      className={`border-b border-white/10 bg-black/20 backdrop-blur-xl ${className}`}
      role="tablist"
      aria-label={ariaLabel}
    >
      <div className="flex items-stretch gap-1 px-3 sm:px-6 overflow-x-auto">
        {groups.map((grp, i) => (
          <div key={grp.label ?? `grp-${i}`} className="flex items-stretch gap-1">
            {grp.label && (
              <span
                className="self-center pl-2 pr-1 text-[10px] font-semibold uppercase tracking-wider text-white/30 whitespace-nowrap select-none"
                aria-hidden="true"
              >
                {grp.label}
              </span>
            )}
            {grp.tabs.map(renderTab)}
          </div>
        ))}
      </div>
    </div>
  );
}

export const SubTabBar = memo(SubTabBarInner);
